import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { makeTranslationsSchema } from '../../common/translations/translations';

export const TEST_TITLE_MAX_LENGTH = 160;
export const TEST_INTRO_MAX_LENGTH = 1000;

/**
 * The bounds an authoring surface has to hold its own controls to.
 *
 * Named rather than left inline, for `TEST_TITLE_MAX_LENGTH`'s reason: an admin
 * editor has to clamp a number box, cap a slider and word a hint, and every one
 * of those spelled `100` is a place the contract can be tightened without the
 * client noticing — a control that offers a value the api refuses, or refuses one
 * it would take.
 */
export const TEST_PASSING_SCORE_MIN_PCT = 1;
export const TEST_PASSING_SCORE_MAX_PCT = 100;
export const TEST_ATTEMPTS_MIN = 1;
export const TEST_ATTEMPTS_MAX = 100;
/** One year, in hours. A cooldown longer than that is a withdrawn test. */
export const TEST_COOLDOWN_HOURS_MAX = 8760;

/** Per-locale test copy (N5, W7). */
export const CourseTestTranslationSchema = z
  .object({
    title: z.string().trim().min(1).max(TEST_TITLE_MAX_LENGTH),
    /**
     * What the member reads on the start screen, before the attempt is spent.
     *
     * Required, and the reason is matrix row 8: the rules that cost an attempt —
     * that abandoning counts, that there is a cooldown, that there are only so
     * many takes — must be stated *somewhere a member reads them first*, and this
     * is that place. The client renders the numbers beside it from the test's own
     * fields, so the copy never has to repeat them and cannot contradict them.
     */
    intro: z.string().trim().min(1).max(TEST_INTRO_MAX_LENGTH),
  })
  .strict();

export type CourseTestTranslation = z.infer<typeof CourseTestTranslationSchema>;

export const CourseTestTranslationsSchema = makeTranslationsSchema(CourseTestTranslationSchema);

/** An unlimited-retake test stores `null`, never a sentinel like 0 or -1. */
export const TestAttemptsAllowedSchema = z
  .number()
  .int()
  .min(TEST_ATTEMPTS_MIN)
  .max(TEST_ATTEMPTS_MAX)
  .nullable();

/**
 * A test — a course's assessment, or a standalone one taken directly.
 *
 * **`courseId` is nullable by design** (matrix row 4), and that is the sharpest
 * edge in this schema: Postgres treats NULLs as distinct, so every uniqueness
 * rule spanning it needs two partial indexes rather than one plain one. The
 * tables carry that argument; it is repeated here because a reader adding a
 * constraint will start from this field.
 */
export const CourseTestSchema = z
  .object({
    id: UuidSchema,
    /** `null` for a standalone test that belongs to no course (matrix row 4). */
    courseId: UuidSchema.nullable(),
    /**
     * The owning course's title in the reader's locale — `null` when the test is
     * standalone, and **only** then.
     *
     * A staff list may not draw a uuid, and resolving one course name per row is
     * the N+1 the query bus and every batched lookup exist to prevent. So the
     * name travels with the row: the admin tests list said *ends a course* /
     * *standalone* until this field existed, because `courseId` alone could
     * honestly say no more than that (P22b2's compromise, now retired).
     *
     * Localized like `title`, so it follows the same `Accept-Language` the rest
     * of the row does; a course with no translation in that locale resolves to
     * an empty string rather than to `null`, which is reserved for "there is no
     * course".
     */
    courseTitle: z.string().nullable(),
    /** The score at or above which the attempt passes. */
    passingScorePct: z
      .number()
      .int()
      .min(TEST_PASSING_SCORE_MIN_PCT)
      .max(TEST_PASSING_SCORE_MAX_PCT),
    /** `null` means unlimited takes. Every started attempt spends one (row 8). */
    attemptsAllowed: TestAttemptsAllowedSchema,
    /** Hours after a finished attempt before another may start (rows 5–6). */
    cooldownHours: z.number().int().min(0).max(TEST_COOLDOWN_HOURS_MAX),
    /**
     * Publication. A deactivated test refuses **new** attempts and lets the ones
     * in flight finish and score — matrix row 20. Stopping them mid-answer would
     * spend an attempt for nothing.
     */
    isActive: z.boolean(),
    questionCount: z.number().int().nonnegative(),
    title: z.string(),
    intro: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export type CourseTest = z.infer<typeof CourseTestSchema>;

/** What the admin edit form reads: every locale, not just the caller's (W7). */
export const CourseTestDetailSchema = CourseTestSchema.extend({
  translations: CourseTestTranslationsSchema,
}).strict();

export type CourseTestDetail = z.infer<typeof CourseTestDetailSchema>;
